import { useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import useHttp from '../../../../hooks/useHttp'
import { selectAuthData } from '../../../../../containers/Login/AuthSelectors'
import { selectCommonFormConfigurationData } from '../../../../../containers/SubscriptionFormPage/SubscriptionFormConfigurationSelectors'
import { selectCommonSubscriptionData } from '../../../../../containers/SubscriptionFormPage/SubscriptionFormSelectors'
import { selectPageConfigurationData } from '../../../../../store/PageConfiguration/PageConfigurationSelectors'
import { selectProviderConfiguration } from '../../../../../containers/SubscriptionFormPage/SubscriptionFormConfigurationSelectors'
import * as Sentry from '@sentry/react'

const useResendEmail = () => {
  const { t } = useTranslation()
  const { authTokenSession } = useSelector(selectAuthData)
  const { sendRequest, getConfig } = useHttp({ token: authTokenSession })
  const { formLoaded, realStatus, id } = useSelector(selectCommonFormConfigurationData)
  const { contractLoaded } = useSelector(selectCommonSubscriptionData)
  const { providerWorkflow, resendEmailAllowed } = useSelector(selectProviderConfiguration)
  const [disabled, setDisabled] = useState(false)
  const [countdown, setCountdown] = useState(60)
  const [buttonLabel, setButtonLabel] = useState(t('subscriptionForm:header.resend_email.default'))
  const [errorMessage, setErrorMessage] = useState(null)
  const [successMessage, setSuccessMessage] = useState(null)
  const {
    resend_email_url: resendEmailUrl,
  } = useSelector(selectPageConfigurationData)

  const showComponent = () => {
    return formLoaded === true
      && contractLoaded === true
      && resendEmailAllowed === true
      && realStatus === 'Z0100'
  }

  const resendEmail = event => {
    setErrorMessage(null)
    setSuccessMessage(null)

    event.preventDefault()
    const headers = getConfig('POST')
    headers.body = { id }

    sendRequest(
      resendEmailUrl,
      headers,
      (json, status) => {
        if (status !== 200) {
          setErrorMessage(json.user_message_error)
          if (import.meta.env.PROD) {
            Sentry.captureException(new Error(json.internal_message_error))
          }
          return
        }

        setDisabled(true)
        setTimeout(() => {
          setDisabled(false)
          setCountdown(60)
          setButtonLabel(t('subscriptionForm:header.resend_email.default'))
        }, 60000)
        setButtonLabel(t('subscriptionForm:header.resend_email.loading'))

        const eventKey = `${providerWorkflow}${id}`

        window.Echo.channel(`resend-email.${eventKey}`)
          .listen('.operation.success', (event) => {
            setButtonLabel(t('subscriptionForm:header.resend_email.success'))
            setSuccessMessage('Email envoyé')
            window.Echo.leaveChannel(`resend-email.${eventKey}`)
          })
          .listen('.operation.failed', (event) => {
            if (import.meta.env.PROD) {
              Sentry.captureException(new Error('Resend email operation failed'), { extra: { event } })
            }
            setErrorMessage(t('subscriptionForm:header.resend_email.error'))
            setButtonLabel(t('subscriptionForm:header.resend_email.default'))
            window.Echo.leaveChannel(`resend-email.${eventKey}`)
          })
      }
    )
  }

  useEffect(() => {
    return () => {
      if (id && providerWorkflow) {
        const eventKey = `${providerWorkflow}${id}`
        window.Echo.leaveChannel(`resend-email.${eventKey}`)
      }
    }
  }, [id, providerWorkflow])

  useEffect(() => {
    let interval

    if (disabled) {
      interval = setInterval(() => {
        setCountdown((prevCount) => prevCount - 1)
        if (countdown === 0) {
          setButtonLabel(t('subscriptionForm:header.resend_email.default'))
        }
      }, 1000)
    }

    return () => {
      clearInterval(interval)
    }
  }, [disabled, countdown, t])

  return {
    t,
    countdown,
    showComponent,
    disabled,
    resendEmail,
    buttonLabel,
    successMessage,
    errorMessage,
  }
}

export default useResendEmail